import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { 
  getUsers, 
  registerUser, 
  deleteUser,
  updateUser,
  getFunctionalAreasFull,
  createFunctionalArea,
  updateFunctionalArea,
  deleteFunctionalArea,
  getStatusesFull,
  createStatus,
  updateStatus,
  deleteStatus,
  importExcel
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Loader2, 
  Users,
  Layers,
  Upload,
  Edit2,
  Trash2,
  User,
  Mail,
  Shield,
  Eye,
  EyeOff,
  CheckSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const roleColors = {
  superadmin: 'bg-red-100 text-red-700',
  ugib: 'bg-violet-100 text-violet-700',
  departamentos: 'bg-blue-100 text-blue-700',
  direccion: 'bg-blue-100 text-blue-700',
  staff: 'bg-slate-100 text-slate-600',
};

const roleLabels = {
  superadmin: 'Superadmin',
  ugib: 'UGIB',
  departamentos: 'Departamentos',
  direccion: 'Direccion',
  staff: 'Staff',
};

const SettingsPage = () => {
  const { user: currentUser, isSuperadmin } = useAuth();
  
  // Users
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [savingUser, setSavingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Functional Areas
  const [areas, setAreas] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [showAreaDialog, setShowAreaDialog] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [areaName, setAreaName] = useState('');
  const [savingArea, setSavingArea] = useState(false);
  const [deletingArea, setDeletingArea] = useState(null);
  
  // Task Statuses
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [statusName, setStatusName] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [deletingStatus, setDeletingStatus] = useState(null);
  
  // Excel Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (isSuperadmin()) {
      loadUsers();
      loadAreas();
      loadStatuses();
    }
  }, []);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error al cargar los usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const response = await getFunctionalAreasFull();
      setAreas(response.data);
    } catch (error) {
      console.error('Error loading areas:', error);
      toast.error('Error al cargar las areas');
    } finally {
      setLoadingAreas(false);
    }
  };

  const loadStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const response = await getStatusesFull();
      setStatuses(response.data);
    } catch (error) {
      console.error('Error loading statuses:', error);
      toast.error('Error al cargar los estados');
    } finally {
      setLoadingStatuses(false);
    }
  };

  // User handlers
  const openNewUserDialog = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', password: '', role: 'staff' });
    setShowPassword(false);
    setShowUserDialog(true);
  };

  const openEditUserDialog = (user) => {
    setEditingUser(user);
    setUserForm({ 
      name: user.name, 
      email: user.email, 
      password: '', 
      role: user.role 
    });
    setShowPassword(false);
    setShowUserDialog(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      toast.error('Nombre y email son requeridos');
      return;
    }

    if (!editingUser && !userForm.password) {
      toast.error('La contrasena es requerida para nuevos usuarios');
      return;
    }

    setSavingUser(true);
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        await updateUser(editingUser.id, updateData);
        toast.success('Usuario actualizado correctamente');
      } else {
        // Create new user
        await registerUser(userForm);
        toast.success('Usuario creado correctamente');
      }
      setShowUserDialog(false);
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '', role: 'staff' });
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el usuario');
    } finally {
      setSavingUser(false);
    }
  };

  const handleCreateUser = async () => {
    handleSaveUser();
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(deletingUser.id);
      toast.success('Usuario eliminado');
      setDeletingUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el usuario');
    }
  };

  // Area handlers
  const openAreaDialog = (area = null) => {
    setEditingArea(area);
    setAreaName(area?.name || '');
    setShowAreaDialog(true);
  };

  const handleSaveArea = async () => {
    if (!areaName.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSavingArea(true);
    try {
      if (editingArea) {
        await updateFunctionalArea(editingArea.id, areaName);
        toast.success('Area actualizada');
      } else {
        await createFunctionalArea(areaName);
        toast.success('Area creada');
      }
      setShowAreaDialog(false);
      loadAreas();
    } catch (error) {
      console.error('Error saving area:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el area');
    } finally {
      setSavingArea(false);
    }
  };

  const handleDeleteArea = async () => {
    try {
      const response = await deleteFunctionalArea(deletingArea.id);
      toast.success(`Area eliminada${response.data.affected_tasks > 0 ? ` (${response.data.affected_tasks} tareas afectadas)` : ''}`);
      setDeletingArea(null);
      loadAreas();
    } catch (error) {
      console.error('Error deleting area:', error);
      toast.error('Error al eliminar el area');
    }
  };

  // Status handlers
  const openStatusDialog = (status = null) => {
    setEditingStatus(status);
    setStatusName(status?.name || '');
    setShowStatusDialog(true);
  };

  const handleSaveStatus = async () => {
    if (!statusName.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSavingStatus(true);
    try {
      if (editingStatus) {
        await updateStatus(editingStatus.id, statusName);
        toast.success('Estado actualizado');
      } else {
        await createStatus(statusName);
        toast.success('Estado creado');
      }
      setShowStatusDialog(false);
      loadStatuses();
    } catch (error) {
      console.error('Error saving status:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el estado');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDeleteStatus = async () => {
    try {
      const response = await deleteStatus(deletingStatus.id);
      toast.success(`Estado eliminado${response.data.affected_tasks > 0 ? ` (${response.data.affected_tasks} tareas afectadas)` : ''}`);
      setDeletingStatus(null);
      loadStatuses();
    } catch (error) {
      console.error('Error deleting status:', error);
      toast.error('Error al eliminar el estado');
    }
  };

  // Excel import
  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    try {
      const response = await importExcel(file);
      setImportResult(response.data);
      toast.success('Importacion completada');
      loadUsers();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  if (!isSuperadmin()) {
    return (
      <Layout>
        <div className="p-6 md:p-10 flex items-center justify-center h-96">
          <div className="text-center text-slate-500">
            <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Acceso restringido</p>
            <p className="text-sm">Solo superadmin puede acceder a esta seccion</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
            Configuracion
          </h1>
          <p className="text-slate-500 mt-1">Administra usuarios y areas funcionales</p>
        </div>

        <Tabs defaultValue="users">
          <TabsList className="bg-stone-100 mb-6">
            <TabsTrigger value="users" className="data-[state=active]:bg-white">
              <Users className="w-4 h-4 mr-2" />
              Usuarios
            </TabsTrigger>
            <TabsTrigger value="areas" className="data-[state=active]:bg-white">
              <Layers className="w-4 h-4 mr-2" />
              Areas
            </TabsTrigger>
            <TabsTrigger value="statuses" className="data-[state=active]:bg-white">
              <CheckSquare className="w-4 h-4 mr-2" />
              Estados
            </TabsTrigger>
            <TabsTrigger value="import" className="data-[state=active]:bg-white">
              <Upload className="w-4 h-4 mr-2" />
              Importar
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Usuarios del Sistema
                </CardTitle>
                <Button
                  onClick={openNewUserDialog}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="new-user-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Usuario
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {loadingUsers ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {users.map((user) => (
                      <div 
                        key={user.id} 
                        className="flex items-center justify-between p-4 hover:bg-stone-50"
                        data-testid={`user-row-${user.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{user.name}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Mail className="w-3 h-3" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={`${roleColors[user.role]} font-bold uppercase text-[10px]`}>
                            {roleLabels[user.role]}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {format(new Date(user.created_at), 'dd MMM yyyy', { locale: es })}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditUserDialog(user)}
                            className="h-8 w-8 text-slate-400 hover:text-slate-600"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {user.id !== currentUser.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeletingUser(user)}
                              className="h-8 w-8 text-slate-400 hover:text-red-600"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Areas Tab */}
          <TabsContent value="areas">
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Areas Funcionales
                </CardTitle>
                <Button
                  onClick={() => openAreaDialog()}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="new-area-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Area
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {loadingAreas ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {areas.map((area) => (
                      <div 
                        key={area.id} 
                        className="flex items-center justify-between p-4 hover:bg-stone-50"
                        data-testid={`area-row-${area.id}`}
                      >
                        <span className="font-medium text-slate-900">{area.name}</span>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openAreaDialog(area)}
                            className="h-8 w-8 text-slate-400 hover:text-slate-600"
                            data-testid={`edit-area-${area.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingArea(area)}
                            className="h-8 w-8 text-slate-400 hover:text-red-600"
                            data-testid={`delete-area-${area.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Statuses Tab */}
          <TabsContent value="statuses">
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Estados de Tareas
                </CardTitle>
                <Button
                  onClick={() => openStatusDialog()}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="new-status-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Estado
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {loadingStatuses ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {statuses.map((status) => (
                      <div 
                        key={status.id} 
                        className="flex items-center justify-between p-4 hover:bg-stone-50"
                        data-testid={`status-row-${status.id}`}
                      >
                        <span className="font-medium text-slate-900">{status.name}</span>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openStatusDialog(status)}
                            className="h-8 w-8 text-slate-400 hover:text-slate-600"
                            data-testid={`edit-status-${status.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingStatus(status)}
                            className="h-8 w-8 text-slate-400 hover:text-red-600"
                            data-testid={`delete-status-${status.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Import Tab */}
          <TabsContent value="import">
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100">
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Importar desde Excel
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div className="bg-stone-50 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-2">Formato del archivo</h4>
                    <p className="text-sm text-slate-600 mb-3">
                      El archivo Excel debe contener las siguientes hojas:
                    </p>
                    <ul className="text-sm text-slate-600 space-y-2">
                      <li><strong>USUARIOS:</strong> nombre, email, password, rol, telefono, cargo</li>
                      <li><strong>AGENTES_EXTERNOS:</strong> nombre, empresa, cargo, telefono, email, notas</li>
                      <li><strong>AGENTES_INTERNOS:</strong> nombre, telefono, email, notas</li>
                    </ul>
                  </div>

                  <div>
                    <Button
                      variant="outline"
                      className="border-stone-200"
                      onClick={() => document.getElementById('excel-upload').click()}
                      disabled={importing}
                      data-testid="import-excel-btn"
                    >
                      {importing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Seleccionar Archivo Excel
                    </Button>
                    <input
                      id="excel-upload"
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={handleImportExcel}
                    />
                  </div>

                  {importResult && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <h4 className="font-medium text-emerald-900 mb-2">Resultado de la importacion</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-emerald-700">Usuarios creados: {importResult.usuarios_creados}</p>
                          {importResult.usuarios_errores?.length > 0 && (
                            <p className="text-red-600 text-xs mt-1">
                              Errores: {importResult.usuarios_errores.length}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-emerald-700">Agentes internos: {importResult.agentes_internos_creados}</p>
                          {importResult.agentes_internos_errores?.length > 0 && (
                            <p className="text-red-600 text-xs mt-1">
                              Errores: {importResult.agentes_internos_errores.length}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-emerald-700">Agentes externos: {importResult.agentes_externos_creados}</p>
                          {importResult.agentes_externos_errores?.length > 0 && (
                            <p className="text-red-600 text-xs mt-1">
                              Errores: {importResult.agentes_externos_errores.length}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* New/Edit User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="sm:max-w-md" data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="Nombre completo"
                className="border-stone-200"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                className="border-stone-200"
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>
                {editingUser ? 'Nueva Contrasena (dejar vacio para mantener)' : 'Contrasena'}
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder={editingUser ? "Nueva contrasena (opcional)" : "Contrasena segura"}
                  className="border-stone-200 pr-10"
                  data-testid="user-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select 
                value={userForm.role} 
                onValueChange={(v) => setUserForm({ ...userForm, role: v })}
              >
                <SelectTrigger className="border-stone-200" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="ugib">UGIB</SelectItem>
                  <SelectItem value="departamentos">Departamentos</SelectItem>
                  <SelectItem value="direccion">Direccion</SelectItem>
                  <SelectItem value="superadmin">Superadmin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveUser} 
              disabled={savingUser}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="create-user-btn"
            >
              {savingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Area Dialog */}
      <Dialog open={showAreaDialog} onOpenChange={setShowAreaDialog}>
        <DialogContent className="sm:max-w-md" data-testid="area-dialog">
          <DialogHeader>
            <DialogTitle>{editingArea ? 'Editar Area' : 'Nueva Area'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre del Area</Label>
              <Input
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                placeholder="Nombre del area funcional"
                className="border-stone-200"
                data-testid="area-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAreaDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveArea} 
              disabled={savingArea}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-area-btn"
            >
              {savingArea ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingArea ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="sm:max-w-md" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle>{editingStatus ? 'Editar Estado' : 'Nuevo Estado'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre del Estado</Label>
              <Input
                value={statusName}
                onChange={(e) => setStatusName(e.target.value)}
                placeholder="Ej: Pendiente, En Proceso, Finalizada..."
                className="border-stone-200"
                data-testid="status-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveStatus} 
              disabled={savingStatus}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-status-btn"
            >
              {savingStatus ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingStatus ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!deletingUser} onOpenChange={() => setDeletingUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Usuario</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion eliminara permanentemente al usuario <strong>{deletingUser?.name}</strong> y 
              su agente vinculado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Area Confirmation */}
      <AlertDialog open={!!deletingArea} onOpenChange={() => setDeletingArea(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Area</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion eliminara el area <strong>{deletingArea?.name}</strong>. 
              Las tareas asociadas mantendran esta area pero no aparecera en el selector.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteArea} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Status Confirmation */}
      <AlertDialog open={!!deletingStatus} onOpenChange={() => setDeletingStatus(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Estado</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion eliminara el estado <strong>{deletingStatus?.name}</strong>. 
              Las tareas con este estado lo mantendran pero no aparecera en el selector.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteStatus} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default SettingsPage;
